import { useState } from 'react'
import { AddIcon } from '@chakra-ui/icons'
import {
    Alert,
    AlertIcon,
    AlertTitle,
    Box,
    Flex,
    Grid,
    GridItem,
    Heading,
    IconButton,
    Spinner,
    Switch,
    Text,
} from '@chakra-ui/react'
import { Route } from '@tanstack/react-router'

import { CollapsableToDrawer } from '@components/CollapsableToDrawer'
import { AlertDialog } from '@components/MatchEventEditor/AlertDialog'
import { EventForm } from '@components/MatchEventEditor/Event'
import { EventTable } from '@components/MatchEventEditor/EventTable'
import { MatchEventsMetaData } from '@components/MatchMetaData/MatchEventsMetaData'

import { useSearchState } from '@hooks/useSearchState'
import { useSelectedMatchDetails } from '@hooks/useSelectedMatchDetails'

import { MatchEditorAlertDialogContextProvider } from '@contexts/MatchEditorAlertDialogContext'
import { MatchEditorFormContextProvider } from '@contexts/MatchEditorFormContext'
import { SelectedEventProvider } from '@contexts/SelectedEventContext'

import { useMatchEventEditorData } from '@queries/useMatchEventEditorData'

import { appIndex } from './App'

export function MatchEventEditor() {
    const [{ eventId: selectedEventId }, setSearchState] = useSearchState()
    const [search] = useSearchState()
    const { isLoading } = useMatchEventEditorData()
    const [isDetailedView, setIsDetailedView] = useState(false)

    const selectedMatchDetails = useSelectedMatchDetails()

    if (!search?.matchId) {
        return (
            <Alert status="info">
                <AlertIcon />
                <AlertTitle>No match selected!</AlertTitle>
                Please select a match to continue.
            </Alert>
        )
    }

    return (
        <SelectedEventProvider>
            <MatchEditorAlertDialogContextProvider>
                <Heading as="h2" size="md" mb={1} minH="10">
                    Meta Data
                </Heading>
                <Box
                    w="100%"
                    alignItems="start"
                    borderStyle="solid"
                    borderWidth="1px"
                    borderColor="blackAlpha.50"
                    borderRadius="16"
                    px={{ base: 4, xl: 8 }}
                    py={4}
                    mb={8}>
                    <CollapsableToDrawer>
                        <MatchEventsMetaData
                            matchId={selectedMatchDetails?.id}
                            metaData={selectedMatchDetails?.events_editor_meta}
                        />
                    </CollapsableToDrawer>
                </Box>
                <Grid
                    minH="100%"
                    minW="100%"
                    templateAreas={'"table event"'}
                    gridTemplateRows={'1fr'}
                    gridTemplateColumns={'660px 1fr'}>
                    <GridItem pr={4} area={'table'}>
                        <Flex justifyContent="space-between" alignContent="center">
                            <Heading as="h2" size="md" mb={1} minH="10">
                                Match Timeline
                            </Heading>
                            <Flex>
                                <Text>Detailed view</Text>
                                <Switch
                                    mt={0.5}
                                    ml={1}
                                    isChecked={isDetailedView}
                                    onChange={e => setIsDetailedView(e.target.checked)}
                                />
                            </Flex>
                        </Flex>
                        {isLoading ? (
                            <Spinner
                                thickness="4px"
                                speed="0.65s"
                                emptyColor="gray.200"
                                color="blue.500"
                                size="xl"
                                m="10rem"
                            />
                        ) : (
                            <Box
                                pb={4}
                                borderStyle="solid"
                                borderWidth="1px"
                                borderColor="blackAlpha.50"
                                borderRadius="16">
                                <EventTable showAllEvents={isDetailedView} />
                            </Box>
                        )}
                    </GridItem>
                    <GridItem area={'event'} maxW="800px">
                        <Box position="sticky" top="20px">
                            <Heading as="h2" size="md" mb={1} minH="10">
                                {selectedEventId ? 'Edit Event' : 'Add Event'}
                                {selectedEventId && (
                                    <IconButton
                                        aria-label="Add event"
                                        colorScheme="teal"
                                        size="xs"
                                        ml={2}
                                        icon={<AddIcon />}
                                        onClick={() => setSearchState({ eventId: undefined })}
                                    />
                                )}
                            </Heading>
                            <Box
                                py={5}
                                px={8}
                                borderStyle="solid"
                                borderWidth="1px"
                                borderColor="blackAlpha.50"
                                borderRadius="16">
                                <MatchEditorFormContextProvider>
                                    <EventForm />
                                </MatchEditorFormContextProvider>
                            </Box>
                            <AlertDialog />
                        </Box>
                    </GridItem>
                </Grid>
            </MatchEditorAlertDialogContextProvider>
        </SelectedEventProvider>
    )
}

export const matchEventEditor = new Route({
    getParentRoute: () => appIndex,
    path: 'editors/match-events',
    component: MatchEventEditor,
})
